import React, { createContext, useState, useEffect } from 'react';
import jwt_decode from 'jwt-decode';
import api from '../utils/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if token exists and is valid on app load
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Check if token is expired
        try {
          const decoded = jwt_decode(token);
          const currentTime = Date.now() / 1000;
          
          if (decoded.exp < currentTime) {
            // Token expired, logout
            logout();
            setIsLoading(false);
            return;
          }
          
          // Set auth token header
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Fetch user data
          try {
            const res = await api.get('/api/auth/me');
            setUser(res.data.user);
            setIsAuthenticated(true);
            setError(null);
          } catch (err) {
            localStorage.removeItem('token');
            setUser(null);
            setIsAuthenticated(false);
            setError('Session expired. Please login again.');
          }
        } catch (err) {
          localStorage.removeItem('token');
          setUser(null);
          setIsAuthenticated(false);
          setError('Invalid token. Please login again.');
        }
      }
      
      setIsLoading(false);
    };

    loadUser();
  }, []);

  // Login
  const login = async (email, password) => {
    try {
      const res = await api.post('/api/auth/login', { email, password });
      
      const { token } = res.data;
      localStorage.setItem('token', token);
      
      // Set auth token header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Decode token to get user data
      const decoded = jwt_decode(token);
      
      // The response from login endpoint now includes user and company data
      // For SuperUser, company may be null which is expected
      setUser({
        ...decoded,
        ...res.data.user,
        company: res.data.company || null
      });
      setIsAuthenticated(true);
      setError(null);
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      return { success: false, error: err.response?.data?.message || 'Login failed' };
    }
  };

  // Register
  const register = async (userData) => {
    try {
      const res = await api.post('/api/auth/register', userData);
      
      const { token } = res.data;
      localStorage.setItem('token', token);
      
      // Set auth token header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Decode token to get user data
      const decoded = jwt_decode(token);
      
      // The response from register endpoint includes user and company data
      // For SuperUser, company may be null which is expected
      setUser({
        ...decoded,
        ...res.data.user,
        company: res.data.company || null
      });
      setIsAuthenticated(true);
      setError(null);
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      return { success: false, error: err.response?.data?.message || 'Registration failed' };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  // Update user
  const updateUser = (userData) => {
    setUser({ ...user, ...userData });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        error,
        login,
        register,
        logout,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
